import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { TrainingAPI } from "../../api/TrainingAPI";
import { ProcessedStatDataContainer } from "./processedStatDataContainer";
import { extractPlayerOrPositionalData, buildOptionGroup } from "./nutritionUtils";
import { useApiCache } from "./useApiCache"; // Adjust path as needed.
import { get_metric_info_from_identifier } from "../../Utils/chart_utils";

// Create an instance of the API to be used in the hook.
const trainingAPI = new TrainingAPI();

/* ==================================================
   Hook: useNutritionMetrics
   ================================================== */

/**
 * Custom hook to process nutrition-related metrics.
 *
 * This hook handles:
 *   - Creating grouped options for players/positions/groups.
 *   - Retrieving raw testing data from the API.
 *   - Converting raw data into a ProcessedStatDataContainer instance.
 *
 * @param {object} params - Parameters (date range, session, selections, etc.).
 * @returns {object} {
 *   playerPosGroupOptions,         // Option groups for selection UI.
 *   retrieveStatMetrics,           // Function to trigger data retrieval.
 *   processedDataContainer,        // Instance of ProcessedStatDataContainer (use .dataList).
 *   processedDataNonEmpty          // Boolean array indicating data completeness per metric.
 * }
 */
export function useNutritionMetrics({
  startDate,
  endDate,
  session,
  playerData,
  groups,
  allPlayers,
  positions,
  statMetrics,
  metricType = "nutrition",
  all_metric_types,
  type,
}) {
  // Define the maximum number of selections allowed.
  const MAX_SELECTION_THRESHOLD = 15;
  // Wrap the API function with caching. Make sure to bind the context if necessary.
  const cachedGetTestingData = useApiCache(trainingAPI.getTestingData.bind(trainingAPI), 300000);
  // Define the state variables.  
  const [playerPosGroupOptions, setPlayerPosGroupOptions] = useState([]);
  const [processedDataContainer, setProcessedDataContainer] = useState(null);
  const [processedDataNonEmpty, setProcessedDataNonEmpty] = useState([]);
  const [playerPosGroupOptionsSelected, setPlayerPosGroupOptionsSelected] = useState(["full-team"]);
  const cacheRef = useRef(new Map());
  // Define the function to handle the selection change.
  const handleOptionGroupLeftChange = (value) => {
    if (value.length > MAX_SELECTION_THRESHOLD) {
      message.warning(`You can't select more than ${MAX_SELECTION_THRESHOLD} players, positions, groups.`);
    } else {
      setPlayerPosGroupOptionsSelected(value);
    }
  };

  // Memoize the option groups so they only update when dependencies change.
  const options = useMemo(() => {
    const groupOptions = [];
    groupOptions.push({
      label: <span>Team</span>,
      title: "Team",
      options: [{ label: <span>Full Team</span>, value: "full-team", key: "full-team" }],
    });
    if (allPlayers && allPlayers.length > 0) {
      groupOptions.push(buildOptionGroup("Players", allPlayers, "player"));
    }
    if (positions && positions.length > 0) {
      groupOptions.push(buildOptionGroup("Positions", positions, "pos"));
    }
    if (groups && groups.length > 0) {
      groupOptions.push(buildOptionGroup("Groups", groups, "group"));
    }
    return groupOptions;
  }, [allPlayers, positions, groups]);

  useEffect(() => {
    setPlayerPosGroupOptions(options);
  }, [options]);

  /**
   * Function to retrieve raw testing data and process it.
   *
   * This function loops over the selection keys, fetches data concurrently,
   * instantiates a ProcessedStatDataContainer, and updates state.
   */
  const retrieveStatMetrics = useCallback(async () => {
    // If no metrics are selected, return.
    if (!statMetrics || statMetrics.length === 0) return;

    const firstStatMetric = statMetrics[0];
    if (!firstStatMetric) return;
    
    const { metric_id } = get_metric_info_from_identifier(firstStatMetric);

    if(!metric_id || metric_id === "-") return;
    
    // Create an array of promises for API calls (using Promise.all for parallelization)
    const promises = playerPosGroupOptionsSelected.map((selectionKey) => {
      const queryDatum = extractPlayerOrPositionalData(selectionKey);
      if (!queryDatum) return Promise.resolve(null);
      return cachedGetTestingData({
          start_date: startDate,
          end_date: endDate,
          metric_type: metricType,
          stats: "",
          metric_id: metric_id,
          ...queryDatum,
          sessions: session,
          multi_metrics: statMetrics.map((metric) => {
            const { metric_id: metricId } = get_metric_info_from_identifier(metric);
            return metricId ? metricId.split("-")[1] : "";
          }).filter(Boolean).join(","),
        })
        .then((response) => ({ [selectionKey]: response }))
        .catch(() => {
          message.error("Error fetching testing data");
          return null;
        });
    });
    // Wait for all API calls to complete.
    const unprocessedStatData = (await Promise.all(promises)).filter((res) => res !== null);

    // Convert the stat metrics to readable labels using the static method.
    const metricLabels = ProcessedStatDataContainer.getMetricLabels(statMetrics, all_metric_types, metricType);

    // Create the container instance.
    const container = new ProcessedStatDataContainer(unprocessedStatData, metricLabels, allPlayers, groups, metricType);

    // Update the state with the container instance.
    setProcessedDataContainer(container);

    // Use the container's method to check whether each metric's data is non-empty.
    const dataNonEmpty = container.getNonEmptyFlags();

    // Update the state with the non-empty flags.
    setProcessedDataNonEmpty(dataNonEmpty);
  }, [startDate, endDate, session, playerData, groups, statMetrics, playerPosGroupOptionsSelected, metricType, all_metric_types]);

  // Use the custom hook to retrieve data when the selection changes.
  useEffect(() => {
    if (type === "team" && statMetrics.length > 0) {
      retrieveStatMetrics().catch((err) => console.error(err));
    }
  }, [playerPosGroupOptionsSelected, startDate, endDate, statMetrics]);

  // For convenience, memoize the processed data list using the container's getter.
  const processedStatDataList = useMemo(() => {
    return processedDataContainer ? processedDataContainer.dataList : [];
  }, [processedDataContainer]);

  return {
    playerPosGroupOptionsSelected,
    playerPosGroupOptions,
    retrieveStatMetrics,
    handleOptionGroupLeftChange,
    processedDataContainer, // Use .dataList for the array of processed data.
    processedStatDataList,      // A memoized alias for processedDataContainer.dataList.
    processedDataNonEmpty,
  };
}
